import { ExternalLink as ExternalLinkIcon } from 'lucide-react';
import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTEF } from 'src/tef/components/providers/TEFProvider';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import type { ComponentSize, ForwardedHTMLProps } from 'src/tef/components/utils/types';

type LinkVariant = 'default' | 'subtle' | 'button' | 'nav';

interface NavigationLinkProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>, ForwardedHTMLProps {
  to: string;
  children: React.ReactNode;
  className?: string;
  external?: boolean;
  variant?: LinkVariant;
  size?: ComponentSize;
  disabled?: boolean;
  active?: boolean;
  showExternalIcon?: boolean;
  prefetch?: boolean;
  replace?: boolean;
}

const sizeClasses: Record<ComponentSize, string> = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
};

export const NavigationLink = React.forwardRef<HTMLAnchorElement, NavigationLinkProps>(
  (
    {
      to,
      children,
      external = false,
      variant = 'default',
      size = 'md',
      disabled = false,
      active: forcedActive,
      showExternalIcon = true,
      replace = false,
      className,
      ...props
    },
    ref
  ) => {
    const tef = useTEF();
    const forwardedProps = forwardProps(props);
    const location = useLocation();

    const isActive = forcedActive ?? (location.pathname === to || location.pathname.startsWith(`${to}/`));

    const linkClasses = useMemo(() => {
      const baseClasses = cn(
        'inline-flex items-center gap-1',
        sizeClasses[size],
        'transition-colors duration-150',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
        'dark:focus-visible:ring-offset-slate-900',
        disabled && 'opacity-50 pointer-events-none cursor-not-allowed'
      );

      const variantClasses: Record<LinkVariant, string> = {
        default: cn('text-primary hover:text-primary/80', 'hover:underline underline-offset-2'),
        subtle: cn(
          tef.colors.foregroundSecondary,
          'hover:text-slate-900 dark:hover:text-slate-100',
          'hover:underline underline-offset-2'
        ),
        button: cn('px-3 py-1.5 rounded-md', 'bg-primary text-white', 'hover:bg-primary/90', 'no-underline'),
        nav: cn(
          'px-3 py-2 rounded-md',
          isActive
            ? 'bg-slate-100 dark:bg-slate-800 text-primary font-medium'
            : cn(tef.colors.foreground, 'hover:bg-slate-50 dark:hover:bg-slate-800/50'),
          'no-underline'
        ),
      };

      return cn(baseClasses, variantClasses[variant], className);
    }, [variant, size, disabled, isActive, tef, className]);

    const content = (
      <>
        {children}
        {external && showExternalIcon && <ExternalLinkIcon className="w-3 h-3 flex-shrink-0" aria-hidden="true" />}
      </>
    );

    if (external) {
      return (
        <a
          ref={ref}
          href={disabled ? undefined : to}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClasses}
          aria-disabled={disabled}
          aria-current={isActive ? 'page' : undefined}
          {...forwardedProps}
          {...props}
        >
          {content}
        </a>
      );
    }

    if (disabled) {
      return (
        <span className={linkClasses} aria-disabled="true" {...forwardedProps}>
          {content}
        </span>
      );
    }

    return (
      <Link
        ref={ref as React.Ref<HTMLAnchorElement>}
        to={to}
        replace={replace}
        className={linkClasses}
        aria-current={isActive ? 'page' : undefined}
        {...forwardedProps}
        {...props}
      >
        {content}
      </Link>
    );
  }
);

NavigationLink.displayName = 'NavigationLink';

export default NavigationLink;
